import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useStartMicrosoftTenantConsent } from '@/hooks';
import { MicrosoftTenantSourceSafe } from '@vivport/backend/types';
import { Copy } from 'lucide-react';
import { useId, useState } from 'react';
import { toast } from 'sonner';
import { startConnectInNewTab } from './start-connect-in-new-tab';

function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString();
}

function statusVariant(status: MicrosoftTenantSourceSafe['status']): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'ACTIVE') {
    return 'default';
  }
  if (status === 'REVOKED') {
    return 'destructive';
  }
  return 'secondary';
}

function PublishingGrant({ applicationClientId, isHubAdmin }: { applicationClientId: string; isHubAdmin: boolean }) {
  const startConsent = useStartMicrosoftTenantConsent();
  const siteInputId = useId();
  const [siteUrl, setSiteUrl] = useState('');
  const command = `Grant-PnPAzureADAppSitePermission -AppId "${applicationClientId}" -DisplayName "Vivport" -Permissions Write -Site "${siteUrl.trim() || '<site url>'}"`;

  const copy = async () => {
    await navigator.clipboard.writeText(command);
    toast.success('Command copied.');
  };

  const rerunConsent = () => {
    startConnectInNewTab({ label: 'SharePoint', start: () => startConsent.mutateAsync() });
  };

  return (
    <div className="space-y-2 border-t pt-3">
      <p className="text-sm font-medium">Publishing</p>
      <p className="text-sm text-muted-foreground">
        Vivport writes only into SharePoint sites a SharePoint admin grants it. Run this one-time command in PnP PowerShell
        for each site Vivport should publish into.
      </p>
      <div className="space-y-1">
        <Label htmlFor={siteInputId}>Site URL</Label>
        <Input
          id={siteInputId}
          value={siteUrl}
          onChange={(event) => setSiteUrl(event.target.value)}
          placeholder="https://contoso.sharepoint.com/sites/projects"
        />
      </div>
      <div className="flex items-center gap-2">
        <code className="flex-1 overflow-x-auto whitespace-nowrap rounded-md bg-muted px-3 py-2 text-sm">{command}</code>
        <Button type="button" variant="outline" size="sm" onClick={() => void copy()}>
          <Copy className="mr-1 h-3.5 w-3.5" />
          Copy
        </Button>
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          When Vivport's permission set changes, the tenant admin must run Consent again once.
        </p>
        {isHubAdmin ? (
          <Button type="button" variant="outline" size="sm" onClick={rerunConsent} disabled={startConsent.isPending}>
            Run consent
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function MicrosoftTenantSourceDetails({
  source,
  applicationClientId,
  isHubAdmin,
}: {
  source: MicrosoftTenantSourceSafe;
  applicationClientId: string;
  isHubAdmin: boolean;
}) {
  return (
    <div className="rounded-md border p-4 space-y-3">
      <div className="flex items-center gap-2">
        <span className="font-medium">{source.name}</span>
        <Badge variant={statusVariant(source.status)}>{source.status}</Badge>
      </div>
      <div className="text-sm text-muted-foreground space-y-1">
        <p>Consented {formatDateTime(source.consentedAt)}</p>
        <p>Last verified {formatDateTime(source.lastVerifiedAt)}</p>
        <p>
          {source.driveCount} drive{source.driveCount === 1 ? '' : 's'}
        </p>
      </div>
      <PublishingGrant applicationClientId={applicationClientId} isHubAdmin={isHubAdmin} />
    </div>
  );
}
